import React, { ReactNode } from 'react';
import './AppNav.css';
import logoIcon from '../assets/logo/icon.svg';
import { useAuth } from '../context/AuthContext';

export interface AppNavProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

interface Tab {
  id: string;
  label: string;
  icon: ReactNode;
  primary?: boolean;
}

/* Six destinations; the four marked primary are the ones that fit a phone
   bottom bar. The rest move into "More" there and show normally on desktop. */
const TABS: Tab[] = [
  {
    id: 'orders', label: 'Orders', primary: true,
    icon: <><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /></>,
  },
  {
    id: 'drivers', label: 'Drivers', primary: true,
    icon: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  },
  {
    id: 'map', label: 'Map', primary: true,
    icon: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>,
  },
  {
    id: 'shops', label: 'Shops', primary: true,
    icon: <><path d="M3 9l1.5-5h15L21 9" /><path d="M4 9v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" /><path d="M9 21v-6h6v6" /></>,
  },
  {
    id: 'applications', label: 'Applications',
    icon: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>,
  },
  {
    id: 'analytics', label: 'Analytics',
    icon: <><line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" /></>,
  },
  {
    id: 'settings', label: 'Settings',
    icon: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
  },
];

const Icon: React.FC<{ children: ReactNode }> = ({ children }) => (
  <svg className="nav__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

export const AppNav: React.FC<AppNavProps> = ({ activeTab, onTabChange }) => {
  const { logout } = useAuth();
  const [moreOpen, setMoreOpen] = React.useState(false);

  const primary = TABS.filter(t => t.primary);
  const secondary = TABS.filter(t => !t.primary);
  const moreActive = secondary.some(t => t.id === activeTab);

  const pick = (id: string) => {
    onTabChange(id);
    setMoreOpen(false);
  };

  return (
    <>
      {/* Desktop / tablet: persistent rail */}
      <nav className="nav" aria-label="Primary">
        <div className="nav__brand">
          <img src={logoIcon} alt="" className="nav__logo"
               onError={e => { e.currentTarget.style.display = 'none'; }} />
          <span className="nav__wordmark">Hypr</span>
        </div>

        <div className="nav__list">
          {TABS.map(({ id, label, icon }) => (
            <button
              key={id}
              type="button"
              className={`nav__item ${activeTab === id ? 'is-active' : ''}`}
              onClick={() => pick(id)}
              aria-current={activeTab === id ? 'page' : undefined}
            >
              <Icon>{icon}</Icon>
              <span className="nav__label">{label}</span>
            </button>
          ))}
        </div>

        <button type="button" className="nav__item nav__logout" onClick={logout}>
          <Icon>
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16 17 21 12 16 7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </Icon>
          <span className="nav__label">Sign out</span>
        </button>
      </nav>

      {/* Phone: bottom bar + More sheet */}
      <nav className="tabbar" aria-label="Primary">
        {primary.map(({ id, label, icon }) => (
          <button
            key={id}
            type="button"
            className={`tabbar__item ${activeTab === id ? 'is-active' : ''}`}
            onClick={() => pick(id)}
            aria-current={activeTab === id ? 'page' : undefined}
          >
            <Icon>{icon}</Icon>
            <span className="tabbar__label">{label}</span>
          </button>
        ))}
        <button
          type="button"
          className={`tabbar__item ${moreActive ? 'is-active' : ''}`}
          onClick={() => setMoreOpen(v => !v)}
          aria-expanded={moreOpen}
        >
          <Icon>
            <circle cx="5" cy="12" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="19" cy="12" r="1.6" />
          </Icon>
          <span className="tabbar__label">More</span>
        </button>
      </nav>

      {moreOpen && (
        <div className="more" onClick={() => setMoreOpen(false)}>
          <div className="more__sheet" onClick={e => e.stopPropagation()}>
            <div className="more__grip" />
            {secondary.map(({ id, label, icon }) => (
              <button
                key={id}
                type="button"
                className={`more__item ${activeTab === id ? 'is-active' : ''}`}
                onClick={() => pick(id)}
              >
                <Icon>{icon}</Icon>
                <span>{label}</span>
              </button>
            ))}
            <button type="button" className="more__item more__item--danger" onClick={logout}>
              <Icon>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </Icon>
              <span>Sign out</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
